import { Select as Base } from "@base-ui/react/select";
import { IconCheck, IconSelector } from "@tabler/icons-react";
import { useMemo, type CSSProperties, type ReactNode } from "react";
import { useFieldLabelId } from "./ui";

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Text for type-ahead and the closed field when the label is not plain text. */
  text?: string;
  disabled?: boolean;
}
export interface SelectGroup<V extends string = string> {
  group: string;
  options: readonly SelectOption<V>[];
}

/**
 * A select on Base UI's Select, in place of the native one: the same look in every browser, type-ahead,
 * arrow keys, Home/End, and options that can hold more than text. Values are strings, as with the native
 * select; the empty string is a real option ("Any", "None") when the list has one, and otherwise shows
 * the placeholder. Inside a `Field` it takes the field's label.
 */
export interface SelectProps<V extends string = string> {
  value: V;
  onChange: (value: V) => void;
  options: readonly (SelectOption<V> | SelectGroup<V>)[];
  /** Shown while nothing is chosen (value "" with no "" option). */
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  /** Needed outside a Field. */
  "aria-label"?: string;
  className?: string;
  style?: CSSProperties;
}

const EMPTY = null;
const toBase = (v: string) => (v === "" ? EMPTY : v);
const isGroup = <V extends string>(o: SelectOption<V> | SelectGroup<V>): o is SelectGroup<V> => "group" in o;

export function Select<V extends string = string>({ value, onChange, options, placeholder, disabled, required, name, id, className, style, ...rest }: SelectProps<V>) {
  const labelId = useFieldLabelId();
  const flat = useMemo(() => options.flatMap((o) => (isGroup(o) ? o.options : [o])), [options]);
  const items = useMemo(() => flat.map((o) => ({ value: toBase(o.value), label: o.label })), [flat]);
  const textOf = (v: string | null) => {
    const o = flat.find((x) => x.value === (v ?? ""));
    return o?.text ?? (typeof o?.label === "string" ? o.label : String(v ?? ""));
  };
  const item = (o: SelectOption<V>) => (
    <Base.Item key={o.value || "∅"} value={toBase(o.value)} disabled={o.disabled} label={o.text ?? (typeof o.label === "string" ? o.label : undefined)} className="sel-item">
      <Base.ItemText className="sel-item-text">{o.label}</Base.ItemText>
      <Base.ItemIndicator className="sel-item-check"><IconCheck size={14} /></Base.ItemIndicator>
    </Base.Item>
  );
  return (
    <Base.Root
      items={items}
      value={toBase(value)}
      onValueChange={(v) => onChange((v ?? "") as V)}
      itemToStringLabel={(v) => textOf(v as string | null)}
      disabled={disabled}
      required={required}
      name={name}
      id={id}
    >
      <Base.Trigger className={`sel${className ? ` ${className}` : ""}`} style={style} aria-labelledby={rest["aria-label"] ? undefined : labelId} aria-label={rest["aria-label"]}>
        <Base.Value className="sel-value" placeholder={placeholder ?? "Choose…"} />
        <Base.Icon className="sel-icon"><IconSelector size={14} /></Base.Icon>
      </Base.Trigger>
      <Base.Portal>
        <Base.Positioner className="sel-positioner" sideOffset={4} alignItemWithTrigger={false}>
          <Base.Popup className="sel-popup">
            <Base.List className="sel-list">
              {options.map((o) => isGroup(o)
                ? (
                  <Base.Group key={o.group} className="sel-group">
                    <Base.GroupLabel className="sel-group-label">{o.group}</Base.GroupLabel>
                    {o.options.map(item)}
                  </Base.Group>
                )
                : item(o))}
            </Base.List>
          </Base.Popup>
        </Base.Positioner>
      </Base.Portal>
    </Base.Root>
  );
}

/** Options from a record of value → label, in its order ({ economy: "Economy", … }). */
export const optionsOf = <V extends string>(labels: Record<V, ReactNode>): SelectOption<V>[] =>
  (Object.entries(labels) as [V, ReactNode][]).map(([value, label]) => ({ value, label }));
